import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, AlertTriangle, CalendarClock, CheckSquare, Square, Wallet, Users, FileText, ArrowRight, RefreshCw, TrendingUp, Target, FileDown, ArrowUpRight, ArrowDownRight, Flag, Pencil, Trophy } from "lucide-react";
import { API } from "@/lib/api";
import { fmtDate } from "@/lib/quote-helpers";
import { useOrgCurrency } from "@/lib/currency-context";
import { exportNodeToPdf, exportTeamReportPdf } from "@/lib/pdf";

const TaskRow = ({ t, onToggle, onOpenCustomer, overdue }) => (
  <div className={`group flex items-center gap-3 rounded-lg border px-3 py-2 transition ${overdue ? "border-red-200 bg-red-50" : "border-[var(--brand-border-light)]"}`} data-testid={`dash-task-${t.id}`}>
    <button onClick={() => onToggle(t)} className="shrink-0 text-[var(--brand-primary)]" title="Als erledigt markieren" data-testid={`dash-task-toggle-${t.id}`}>
      <Square className="w-5 h-5 text-[var(--brand-muted)] hover:text-[var(--brand-primary)]" />
    </button>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{t.title}</div>
      <button onClick={() => onOpenCustomer(t)} className="text-[11px] text-[var(--brand-muted)] hover:text-[var(--brand-primary)] truncate flex items-center gap-1" data-testid={`dash-task-cust-${t.id}`}>
        <Users className="w-3 h-3" /> {t.customer_name || "—"}
      </button>
    </div>
    <span className={`text-[11px] shrink-0 ${overdue ? "text-red-600 font-medium" : "text-[var(--brand-muted)]"}`}>{fmtDate(t.due)}</span>
  </div>
);

const TeamReportCard = ({ company }) => {
  const { formatMoney } = useOrgCurrency();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rep, setRep] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    axios.get(`${API}/team/sales-report?month=${month}`)
      .then((r) => { setRep(r.data); setHidden(false); })
      .catch(() => setHidden(true));
  }, [month]);
  if (hidden || !rep) return null;
  const rows = rep.members || [];
  return (
    <Card className="p-5 border-[var(--brand-border)]" data-testid="dash-team-report-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[var(--brand-primary)]" />
          <span className="font-semibold text-[var(--brand-ink)]">Team-Leistung &amp; Provisionen</span>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="border border-[var(--brand-border)] rounded px-2 py-1 text-sm" data-testid="dash-team-report-month" />
          <Button size="sm" variant="outline" onClick={() => exportTeamReportPdf(company, rep.month, rows)}
            className="border-[var(--brand-border)] h-8" data-testid="dash-team-report-pdf">
            <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[var(--brand-muted)]">
              <th className="py-1.5 pr-3">Mitarbeiter</th>
              <th className="py-1.5 pr-3 text-right">Kunden</th>
              <th className="py-1.5 pr-3 text-right">Offene Pipeline</th>
              <th className="py-1.5 pr-3 text-right">Gewonnen ({rep.month})</th>
              <th className="py-1.5 pr-3 text-right">Satz</th>
              <th className="py-1.5 text-right">Provision</th>
            </tr>
          </thead>
          <tbody data-testid="dash-team-report-rows">
            {rows.map((m) => (
              <tr key={m.email || "unassigned"} className="border-t border-[var(--brand-border-light)]" data-testid={`dash-team-report-row-${m.email || "unassigned"}`}>
                <td className="py-2 pr-3">
                  <div className="font-medium text-[var(--brand-ink)]">{m.name || m.email || "Nicht zugeordnet"}</div>
                  {m.email && <div className="text-[11px] text-[var(--brand-muted)]">{m.email}</div>}
                </td>
                <td className="py-2 pr-3 text-right text-[var(--brand-muted)]">{m.customers_assigned}</td>
                <td className="py-2 pr-3 text-right">{formatMoney(m.open_value)}</td>
                <td className="py-2 pr-3 text-right font-medium">{m.won_count}× · {formatMoney(m.won_value)}</td>
                <td className="py-2 pr-3 text-right text-[var(--brand-muted)]">{m.commission_rate} %</td>
                <td className="py-2 text-right font-semibold text-[var(--brand-primary)]">{formatMoney(m.commission)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[var(--brand-muted)] mt-2">Provision = gewonnener Deal-Wert im Monat × Provisionssatz (Satz in Einstellungen → Team pflegen; Zuordnung am Deal/Kunden).</p>
    </Card>
  );
};

export const DashboardTab = ({ company, setTab, openQuoteById }) => {
  const { formatMoney } = useOrgCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [goalEdit, setGoalEdit] = useState(null);
  const [savingGoal, setSavingGoal] = useState(false);
  const reportRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/summary`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleTask = async (t) => {
    if (!t.customer_id) { setTab("kunden"); return; }
    try {
      await axios.post(`${API}/customers/${t.customer_id}/task/${t.id}/toggle`);
      toast.success("Aufgabe erledigt");
      load();
    } catch { toast.error("Fehlgeschlagen"); }
  };
  const openCustomer = () => setTab("kunden");

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      await axios.post(`${API}/settings/goal`, { monthly_revenue_target: Number(goalEdit) || 0 });
      toast.success("Monatsziel gespeichert");
      setGoalEdit(null);
      load();
    } catch { toast.error("Ziel konnte nicht gespeichert werden"); }
    finally { setSavingGoal(false); }
  };

  const exportPdf = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const now = new Date();
      const stamp = now.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
      const title = `${company?.company_name || "Report"} · Monatsreport ${stamp}`;
      await exportNodeToPdf(reportRef.current, `Monatsreport_${now.toISOString().slice(0, 10)}.pdf`, title);
      toast.success("PDF-Report erstellt");
    } catch (e) {
      toast.error("PDF-Export fehlgeschlagen");
    } finally { setExporting(false); }
  };

  if (loading) return <Card className="p-8 border-[var(--brand-border)]"><p className="text-sm text-[var(--brand-muted)]">Lädt Übersicht …</p></Card>;
  if (!data) return <Card className="p-8 border-[var(--brand-border)]"><p className="text-sm text-[var(--brand-muted)]">Übersicht nicht verfügbar.</p></Card>;

  const { tasks, invoices, stats } = data;
  const hasTasks = (tasks.overdue.length + tasks.today.length + tasks.week.length) > 0;
  const revenue = data.revenue_series || [];
  const maxRev = Math.max(1, ...revenue.map((r) => r.amount));
  const pipeline = data.pipeline || null;
  const cmp = data.comparison || null;
  const goal = data.goal || null;
  const stageColors = { neu: "#3B82F6", angebot: "#8B5CF6", verhandlung: "#F59E0B", gewonnen: "#10B981", verloren: "#EF4444" };
  const maxStageVal = pipeline ? Math.max(1, ...pipeline.by_stage.map((s) => s.value)) : 1;

  const DeltaBadge = ({ d, testid }) => {
    if (!d) return null;
    const up = d.prev_new || d.delta_pct > 0;
    const flat = !d.prev_new && d.delta_pct === 0;
    const color = flat ? "var(--brand-muted)" : up ? "#10B981" : "#DC2626";
    const label = d.prev_new ? "neu" : `${d.delta_pct > 0 ? "+" : ""}${d.delta_pct}%`;
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold" style={{ color }} data-testid={testid} title="im Vergleich zum Vormonat">
        {!flat && (up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />)} {label}
        <span className="text-[10px] font-normal text-[var(--brand-muted)] ml-0.5">vs. Vormonat</span>
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="dashboard-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="w-6 h-6 text-[var(--brand-primary)]" />
          <div>
            <h1 className="font-[Playfair_Display] text-2xl font-semibold text-[var(--brand-ink)]">Übersicht</h1>
            <p className="text-sm text-[var(--brand-muted)]">{company?.company_name ? `Willkommen zurück – ${company.company_name}` : "Alles Wichtige auf einen Blick"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting} className="border-[var(--brand-border)]" data-testid="dash-export-pdf"><FileDown className="w-4 h-4 mr-1" /> {exporting ? "Erstellt …" : "PDF-Report"}</Button>
          <Button variant="outline" size="sm" onClick={load} className="border-[var(--brand-border)]" data-testid="dash-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren</Button>
        </div>
      </div>

      <div ref={reportRef} className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-kpis">
        <Card className="p-4 border-[var(--brand-border)] cursor-pointer hover:border-[var(--brand-primary)] transition" onClick={() => setTab("list")}>
          <div className="text-xs text-[var(--brand-muted)] flex items-center gap-1"><Wallet className="w-3 h-3" /> Offener Betrag</div>
          <div className={`text-2xl font-bold ${invoices.open_amount > 0 ? "text-amber-600" : "text-[var(--brand-ink)]"}`}>{formatMoney(invoices.open_amount)}</div>
        </Card>
        <Card className={`p-4 border-[var(--brand-border)] cursor-pointer transition ${invoices.overdue_amount > 0 ? "bg-red-50 border-red-200 hover:border-red-400" : "hover:border-[var(--brand-primary)]"}`} onClick={() => setTab("list")} data-testid="dash-kpi-overdue">
          <div className="text-xs text-[var(--brand-muted)] flex items-center gap-1">{invoices.overdue_amount > 0 && <AlertTriangle className="w-3 h-3 text-red-500" />} Überfällig</div>
          <div className={`text-2xl font-bold ${invoices.overdue_amount > 0 ? "text-red-600" : "text-[var(--brand-ink)]"}`}>{formatMoney(invoices.overdue_amount)}</div>
          <div className="text-[11px] text-[var(--brand-muted)]">{invoices.overdue.length} Rechnung{invoices.overdue.length === 1 ? "" : "en"}</div>
        </Card>
        <Card className="p-4 border-[var(--brand-border)] cursor-pointer hover:border-[var(--brand-primary)] transition" onClick={() => setTab("list")}>
          <div className="text-xs text-[var(--brand-muted)] flex items-center gap-1"><FileText className="w-3 h-3" /> Offene Angebote</div>
          <div className="text-2xl font-bold text-[var(--brand-ink)]">{stats.open_quotes}</div>
        </Card>
        <Card className="p-4 border-[var(--brand-border)] cursor-pointer hover:border-[var(--brand-primary)] transition" onClick={() => setTab("kunden")}>
          <div className="text-xs text-[var(--brand-muted)] flex items-center gap-1"><Users className="w-3 h-3" /> Kunden</div>
          <div className="text-2xl font-bold text-[var(--brand-ink)]">{stats.customer_count}</div>
        </Card>
      </div>

      {/* Umsatz-Kurve (letzte 6 Monate) */}
      <Card className="p-5 border-[var(--brand-border)]" data-testid="dash-revenue-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[var(--brand-primary)]" />
            <span className="font-semibold text-[var(--brand-ink)]">Umsatz – letzte 6 Monate</span>
          </div>
          <div className="flex items-center gap-3">
            {cmp && <DeltaBadge d={cmp.revenue} testid="dash-revenue-delta" />}
            <span className="text-sm font-semibold text-[var(--brand-ink)]" data-testid="dash-revenue-total">{formatMoney(stats.revenue_6m || 0)}</span>
          </div>
        </div>
        <div className="flex items-end gap-2 sm:gap-4 h-40" data-testid="dash-revenue-chart">
          {revenue.map((r) => (
            <div key={r.month} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end" title={`${r.label}: ${formatMoney(r.amount)}`}>
              <div className="text-[10px] text-[var(--brand-muted)] font-medium h-4">{r.amount > 0 ? formatMoney(r.amount).replace(/\s?(€|EUR|CHF)/g, "") : ""}</div>
              <div className="w-full flex items-end" style={{ height: "100%" }}>
                <div
                  className="w-full rounded-t-md bg-[var(--brand-primary)] transition-all"
                  style={{ height: `${Math.max(r.amount > 0 ? 4 : 0, (r.amount / maxRev) * 100)}%` }}
                  data-testid={`dash-revenue-bar-${r.month}`}
                />
              </div>
              <div className="text-[11px] text-[var(--brand-muted)]">{r.label}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Monatsziel Umsatz */}
      {goal && (
        <Card className="p-5 border-[var(--brand-border)]" data-testid="dash-goal-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-[var(--brand-primary)]" />
              <span className="font-semibold text-[var(--brand-ink)]">Monatsziel Umsatz</span>
            </div>
            {goalEdit === null ? (
              <button onClick={() => setGoalEdit(String(goal.monthly_revenue_target || ""))} className="text-xs text-[var(--brand-primary)] hover:underline flex items-center gap-1" data-testid="dash-goal-edit"><Pencil className="w-3 h-3" /> Ziel bearbeiten</button>
            ) : (
              <div className="flex items-center gap-2">
                <input type="number" min="0" step="50" value={goalEdit} onChange={(e) => setGoalEdit(e.target.value)} placeholder="z. B. 10000" className="w-28 border border-[var(--brand-border)] rounded px-2 py-1 text-sm" data-testid="dash-goal-input" autoFocus />
                <Button size="sm" onClick={saveGoal} disabled={savingGoal} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white h-8" data-testid="dash-goal-save">{savingGoal ? "…" : "Speichern"}</Button>
                <button onClick={() => setGoalEdit(null)} className="text-xs text-[var(--brand-muted)]" data-testid="dash-goal-cancel">Abbrechen</button>
              </div>
            )}
          </div>
          {goal.monthly_revenue_target > 0 ? (
            <>
              <div className="flex items-end justify-between mb-1">
                <span className="text-sm text-[var(--brand-muted)]">{formatMoney(goal.current_revenue)} von {formatMoney(goal.monthly_revenue_target)}</span>
                <span className={`text-lg font-bold ${goal.progress_pct >= 100 ? "text-[#10B981]" : "text-[var(--brand-ink)]"}`} data-testid="dash-goal-pct">{goal.progress_pct}%</span>
              </div>
              <div className="h-3 w-full rounded-full bg-[var(--brand-border-light)] overflow-hidden" data-testid="dash-goal-bar">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, goal.progress_pct)}%`, background: goal.progress_pct >= 100 ? "#10B981" : "var(--brand-primary)" }} />
              </div>
              <div className="text-[11px] text-[var(--brand-muted)] mt-1.5">
                {goal.progress_pct >= 100 ? "🎉 Ziel erreicht! Starke Leistung." : `Noch ${formatMoney(Math.max(0, goal.monthly_revenue_target - goal.current_revenue))} bis zum Ziel diesen Monat.`}
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--brand-muted)]" data-testid="dash-goal-empty">Noch kein Monatsziel gesetzt. Lege ein Umsatzziel fest, um deinen Fortschritt zu verfolgen.</p>
          )}
        </Card>
      )}

      {/* Vertriebs-Pipeline */}
      {pipeline && (
        <Card className="p-5 border-[var(--brand-border)]" data-testid="dash-pipeline-card">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-[var(--brand-primary)]" />
            <span className="font-semibold text-[var(--brand-ink)]">Vertriebs-Pipeline</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
            <div className="space-y-4">
              <div>
                <div className="text-xs text-[var(--brand-muted)]">Offener Pipeline-Wert</div>
                <div className="text-2xl font-bold text-[var(--brand-ink)]" data-testid="dash-pipeline-open">{formatMoney(pipeline.open_value)}</div>
                <div className="text-[11px] text-[var(--brand-muted)]">{pipeline.open_count} offene Deal{pipeline.open_count === 1 ? "" : "s"}</div>
              </div>
              {pipeline.forecast && (
                <div data-testid="dash-forecast">
                  <div className="text-xs text-[var(--brand-muted)] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Umsatzprognose (gewichtet)</div>
                  <div className="text-2xl font-bold text-[var(--brand-primary)]" data-testid="dash-forecast-value">{formatMoney(pipeline.forecast.weighted_value)}</div>
                  <div className="text-[11px] text-[var(--brand-muted)]" title="Wahrscheinlichkeit je Phase × Dealwert">
                    {pipeline.forecast.by_stage.map((s) => `${s.label} ${s.probability}%`).join(" · ")}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-[var(--brand-muted)]">Gewonnen-Quote</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[#10B981]" data-testid="dash-pipeline-conversion">{pipeline.conversion_rate}%</span>
                  <span className="text-[11px] text-[var(--brand-muted)]">{pipeline.won_count} gewonnen · {pipeline.lost_count} verloren</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-[var(--brand-border-light)] overflow-hidden">
                  <div className="h-full rounded-full bg-[#10B981]" style={{ width: `${pipeline.conversion_rate}%` }} />
                </div>
                <div className="text-[11px] text-[var(--brand-muted)] mt-1">Gewonnener Umsatz: {formatMoney(pipeline.won_value)}</div>
                {cmp && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="text-[11px] text-[var(--brand-muted)]">Gewonnene Deals ({cmp.won_deals.current}):</span>
                    <DeltaBadge d={cmp.won_deals} testid="dash-won-delta" />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2" data-testid="dash-pipeline-stages">
              {pipeline.by_stage.map((s) => (
                <div key={s.stage} className="flex items-center gap-3" data-testid={`dash-pipeline-stage-${s.stage}`}>
                  <span className="w-20 sm:w-24 text-xs text-[var(--brand-ink)] shrink-0">{s.label}</span>
                  <div className="flex-1 h-6 rounded bg-[var(--brand-border-light)] overflow-hidden">
                    <div className="h-full rounded transition-all" style={{ width: `${Math.max(s.value > 0 ? 6 : 0, (s.value / maxStageVal) * 100)}%`, background: stageColors[s.stage] }} />
                  </div>
                  <span className="w-10 text-right text-[11px] text-[var(--brand-muted)] shrink-0">{s.count}×</span>
                  <span className="w-20 sm:w-24 text-right text-xs font-medium text-[var(--brand-ink)] shrink-0">{formatMoney(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aufgaben */}
        <Card className="p-5 border-[var(--brand-border)]" data-testid="dash-tasks-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[var(--brand-primary)]" />
              <span className="font-semibold text-[var(--brand-ink)]">Fällige Aufgaben</span>
            </div>
            <button onClick={() => setTab("kalender")} className="text-xs text-[var(--brand-primary)] hover:underline flex items-center gap-1" data-testid="dash-goto-calendar">Kalender <ArrowRight className="w-3 h-3" /></button>
          </div>
          {!hasTasks ? (
            <p className="text-sm text-[var(--brand-muted)]" data-testid="dash-tasks-empty">Keine offenen Aufgaben in den nächsten 7 Tagen. 🎉 Lege Follow-ups im Kundenprofil an.</p>
          ) : (
            <div className="space-y-4">
              {tasks.overdue.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-red-600 font-semibold mb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Überfällig ({tasks.overdue.length})</div>
                  <div className="space-y-1.5">{tasks.overdue.map((t) => <TaskRow key={t.id} t={t} onToggle={toggleTask} onOpenCustomer={openCustomer} overdue />)}</div>
                </div>
              )}
              {tasks.today.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[var(--brand-primary)] font-semibold mb-1.5">Heute ({tasks.today.length})</div>
                  <div className="space-y-1.5">{tasks.today.map((t) => <TaskRow key={t.id} t={t} onToggle={toggleTask} onOpenCustomer={openCustomer} />)}</div>
                </div>
              )}
              {tasks.week.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[var(--brand-muted)] font-semibold mb-1.5">Diese Woche ({tasks.week.length})</div>
                  <div className="space-y-1.5">{tasks.week.map((t) => <TaskRow key={t.id} t={t} onToggle={toggleTask} onOpenCustomer={openCustomer} />)}</div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Überfällige Rechnungen */}
        <Card className="p-5 border-[var(--brand-border)]" data-testid="dash-overdue-card">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="font-semibold text-[var(--brand-ink)]">Überfällige Rechnungen</span>
          </div>
          {invoices.overdue.length === 0 ? (
            <p className="text-sm text-[var(--brand-muted)]" data-testid="dash-overdue-empty">Keine überfälligen Rechnungen – alles im grünen Bereich. ✅</p>
          ) : (
            <div className="space-y-1.5" data-testid="dash-overdue-list">
              {invoices.overdue.map((d) => (
                <button key={d.id} onClick={() => openQuoteById && openQuoteById(d.id)}
                  className="w-full flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 hover:border-red-400 transition text-left"
                  data-testid={`dash-overdue-${d.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{d.angebot_nr} · {d.kunde}</div>
                    <div className="text-[11px] text-red-600 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> fällig seit {fmtDate(d.faellig)}</div>
                  </div>
                  <span className="text-sm font-semibold text-[var(--brand-ink)] shrink-0">{formatMoney(d.amount, d.currency)}</span>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Top-Kunden nach Umsatz */}
        <Card className="p-5 border-[var(--brand-border)]" data-testid="dash-top-customers-card">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-[var(--brand-primary)]" />
            <span className="font-semibold text-[var(--brand-ink)]">Top-Kunden nach Umsatz</span>
          </div>
          {(!data.top_customers || data.top_customers.length === 0) ? (
            <p className="text-sm text-[var(--brand-muted)]" data-testid="dash-top-customers-empty">Noch keine fakturierten Umsätze – hier erscheinen deine profitabelsten Kunden.</p>
          ) : (
            <div className="space-y-1.5" data-testid="dash-top-customers-list">
              {data.top_customers.map((c, i) => (
                <div key={c.name} className="flex items-center gap-3 rounded-lg border border-[var(--brand-border-light)] px-3 py-2" data-testid={`dash-top-customer-${i}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${i === 0 ? "bg-[var(--brand-primary)] text-white" : "bg-[var(--brand-border-light)] text-[var(--brand-ink)]"}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{c.name}</div>
                    <div className="text-[11px] text-[var(--brand-muted)]">{c.count} Rechnung{c.count === 1 ? "" : "en"}</div>
                  </div>
                  <span className="text-sm font-semibold text-[var(--brand-ink)] shrink-0">{formatMoney(c.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <TeamReportCard company={company} />
      </div>
    </div>
  );
};
